/**
 * engine/igh.ts — Índice de Garantia Hipotecária e série de garantias mês a mês.
 *
 * Cópia LITERAL de fincast-viabilidade-v67.html:
 *   medAcumAt (1593) · ymIGH (1594) · calcIGH (1596-1615) · ighSerie (1618-1631)
 *   ighAvalia (1632-1636) · serieGarantias (2001-2032)
 *
 * ⚠ EXCEÇÃO À LITERALIDADE (29/07/2026 — Parte B do IGH, aprovada pelo usuário):
 * `calcIGH` deixou de ser literal. A data de registro passou de porta de ENTRADA a porta de
 * SAÍDA do estoque e passou a ser `assinatura + EL-146 dias`; as vagas avulsas entraram.
 * Ver `_saiuEstoqueTempo`. Todo o resto do arquivo segue literal.
 * E em 09/10/2026 (lote 2, item 20) a data de assinatura passou a ser a EFETIVA: quem
 * ingressa para a demanda assina no "Cumprimento de demanda" — ver `dataFinanciamentoEfetiva`.
 *
 * ⚠ O FSF DA BARRA FOI UNIFICADO (31/07/2026, a pedido: "garanta que a lógica da fórmula
 * está igual em todos os lugares"). `serieGarantias` usava `rep × preço × %`, sem descontar
 * a fração do terreno — o gráfico mês a mês contava uma história diferente do KPI da tela
 * de Premissas. Agora passa por `repasseDaUnidade`, a mesma função do estático e do fluxo.
 *
 * ⚠ LOTE 2 DE 09/10/2026 (item 16, "Unidade a unidade (Recomendado)"): com o Detalhamento
 * preenchido, FSF e TERRENO da barra somam unidade a unidade pela MESMA conta da tela de
 * Premissas (`fsfDetalheDe`) — ver `_fsfTerrenoDaBarra`. A média (preço médio, % padrão,
 * fração média) ficou para o Detalhamento zerado (item 26) e para o excedente das "Vendas
 * Fin." sem unidade identificada. É o caminho que a planilha da Caixa (MO 3041) usa — e o
 * que ela confere (`tests/caixa-mo3041.test.ts`, tabelas vazias) não mudou.
 *
 * ⚠ LOTE 4 DE 10/10/2026, ITEM 1: o "IGH pós-demanda" (`ighAvalia`) só olha os meses a partir
 * do "Cumprimento de demanda" — ver `ighPosDemandaDesde`. A série do EL-078 (`ighSerie`) não muda.
 */

import { addDays, ymOf } from './datas';
import { _vagaExcl, baseDaVaga, baseDet, dataFinanciamentoEfetiva, vendasDetZeradas, vendasMensais } from './demanda';
import { fsfDetalheDe, ladoDetalhamento, repasseDaUnidade, tetoInformadoPJ } from './garantias';
import type {
  AvaliacaoIGH, BarraGarantia, DetalheBarra, DetalheFSF, Empreendimento, Lev, OpcoesIGH, PontoIGH,
  ResultadoBase, ResultadoIGH, Unidade, Ym,
} from './tipos';

/** Só uma data 'YYYY-MM-DD' pode ser somada com segurança (`addDays` estoura no resto). */
const ISO_COMPLETA = /^\d{4}-\d{2}-\d{2}$/;

/* `dataFinanciamentoEfetiva` (lote 2 de 09/10/2026, item 20) MUDOU-SE para engine/demanda.ts na revisão
   do lote 2 (10/10/2026): o predicado do Detalhamento zerado (`vendasDetZeradas`, item 26) também a lê, e
   demanda.ts não pode importar daqui (igh.ts já importa de lá). Reexportada aqui para os chamadores de antes. */
export { dataFinanciamentoEfetiva };

/**
 * MÊS DO REGISTRO do contrato de financiamento da unidade — 29/07/2026, Parte B do IGH.
 *
 * A coluna "Data de Financiamento" do EL-147 (`u.dataRegistro`, cujo NOME DE CAMPO MENTE)
 * guarda a data de **assinatura** do contrato, não a do registro. O registro sai
 * `lev.tempoRegistro` dias depois — é exatamente o que o EL-146 mede ("Tempo médio, em
 * dias, que uma unidade leva para ser registrada após a assinatura do contrato de
 * financiamento"). O EL-146 está na tela desde a v39 e o motor NUNCA o havia lido.
 *
 * `null` = sem contrato assinado → o registro nunca acontece → a unidade não sai do estoque.
 *
 * ⚠ 09/10/2026 (lote 2, item 20): a data lida é a EFETIVA (`dataFinanciamentoEfetiva`) —
 * quem ingressa para a demanda assina no "Cumprimento de demanda" (EL-065), não no
 * `u.dataRegistro` gravado.
 *
 * ⚠ Data malformada (importação suja) NÃO estoura: cai em `ymOf`, que devolve `NaN`, e
 * `NaN <= ymD` é `false` — a unidade fica em estoque. Chamar `addDays` direto lançaria
 * `RangeError` e derrubaria o cálculo inteiro.
 */
export function ymRegistroUnidade(lev: Lev, u: Unidade): Ym | null {
  const d = dataFinanciamentoEfetiva(lev, u);
  if (!d) return null;
  const dias = (lev && lev.tempoRegistro) || 0;
  if (dias === 0 || !ISO_COMPLETA.test(d)) return ymOf(d);
  return ymOf(addDays(d, dias));
}

/**
 * A RÉGUA DE SAÍDA DO ESTOQUE no tempo (EL-120) — 29/07/2026, Parte B do IGH.
 *
 * Regra de negócio (literal do usuário): "tanto vagas quanto unidades só saem do estoque
 * quando o contrato de financiamento que sucede a compra e venda da unidade + vaga é
 * registrado no Registro de Imóveis" — e, no EL-120, "é necessário que seja somado o tempo
 * de EL-146 à data de assinatura da unidade".
 *
 * ⚠ MUDANÇA DE POLARIDADE, decidida pelo usuário em 29/07/2026 (caixa de calibração).
 * Até aqui a data era porta de ENTRADA: `eleg = registro ≤ ymD` fazia a unidade só passar a
 * valer como garantia DEPOIS da data, e quem não tinha "Data de Financiamento" preenchida
 * (o caso normal de quem não vendeu) valia ZERO de estoque para sempre — medido nos casos
 * S25a/S28, que ficavam em R$ 0 nos 18 meses contra um mínimo de R$ 10–17 milhões. Agora é
 * porta de SAÍDA: o estoque nasce cheio e decai conforme os contratos são registrados, que
 * é a mesma polaridade que `ighSerie` (o gráfico do EL-078) sempre teve.
 *
 * Consequência declarada: a "Data de venda" DEIXA de tirar a unidade do estoque — vender
 * não basta, só o registro tira. Era o antigo `repassada = financiada && venda ≤ ymD`.
 *
 * `permInfBanco` (EL-114) sai de tudo, pela mesma regra da v54/v55 que já vale no VGV, no
 * IGH estático e em `ighSerie` (que desconta `b.permutadas`).
 */
export function _saiuEstoqueTempo(lev: Lev, u: Unidade, ymD: Ym | null): boolean {
  if (u.permInfBanco) return true;                       /* EL-114: permuta informada ao banco */
  const reg = ymRegistroUnidade(lev, u);
  return reg != null && ymD != null && reg <= ymD;       /* registro = assinatura + EL-146 dias */
}

/**
 * v67 linha 1593 — medição acumulada (pls0 + Σ obra até `ymD`), capada em 1.
 * ⚠ CÓDIGO MORTO no v67 (nenhum chamador). Migrada por ser pura e barata.
 */
export function medAcumAt(lev: Lev, ymD: Ym | null): number {
  let ac = (lev.pls0Pct || 0) / 100;
  (lev.meses || []).forEach(m => { if (ymD == null || m.ym <= ymD) ac += (m.obra || 0) / 100; });
  return Math.min(1, ac);
}

/**
 * v67 linha 1594 — ym da medição do IGH: `dataMedicaoIGH`, senão `c.ymDesejada`, senão `c.ymNorm`.
 * ⚠ CÓDIGO MORTO no v67 (nenhum chamador).
 */
export function ymIGH(lev: Lev, c: ResultadoBase | null | undefined): Ym | null {
  return lev.dataMedicaoIGH ? ymOf(lev.dataMedicaoIGH) : (c && c.ymDesejada != null ? c.ymDesejada : (c ? c.ymNorm : null));
}

/**
 * v67 linhas 1596-1615 — IGH real do md "Cálculo IGH" (coef. 1,30).
 *
 * ⚠ NÃO é mais cópia literal: a Parte B (29/07/2026) trocou a régua do estoque — ver
 * `_saiuEstoqueTempo`. É a primeira divergência DELIBERADA do motor em relação ao v67, e
 * `tests/golden/motor.test.ts` a declara caso a caso.
 *
 * ⚠ LOTE 2 DE 09/10/2026, ITEM 26 — o interruptor e o caminho SEM Detalhamento mudaram:
 *   - o interruptor era "a tabela do Detalhamento tem LINHAS" (`unidadesDet.length > 0`). Abrir
 *     o Detalhar cria uma linha em branco por unidade, e bastava isso para o estoque virar a
 *     soma de linhas vazias (zero em todo mês) — ou o VGV cheio que nunca decai, com linhas só
 *     com valores. Agora é `vendasDetZeradas`: o caminho unidade a unidade só vale quando
 *     alguma unidade não permutada tem "Data de venda" ou "Data de Financiamento";
 *   - sem isso, o estoque deixa de ser "as garantias totais da barra" (o `estoqueFallback`,
 *     herança do protótipo, que não é estoque de unidades) e passa a ser a MESMA régua do
 *     gráfico do EL-078 (`ighSerie`): (unidades − vendas financiadas das Premissas Mensais até
 *     o mês − permutadas) × VGV ÷ unidades — ver `_estoquePelaContagem`. Só vendas FINANCIADAS
 *     tiram do estoque, no mês da venda (decisão de 09/10/2026, "Sim, para todos").
 *
 * @param b    o empreendimento: unidades, VGV e permutadas, para o caminho sem Detalhamento.
 *             Pode vir nulo (os testes mínimos do caminho unidade a unidade não precisam dele).
 *             Até 09/10/2026 era um PARÂMETRO MORTO, como no protótipo.
 * @param lev  usa `lev.unidadesDet` + `lev.vagasDet` (tabela EL-147/EL-158), `lev.tempoRegistro`
 *             e, sem Detalhamento, `lev.meses` (as "Vendas Fin." das Premissas Mensais).
 *
 * O coeficiente 1.30 fica LITERAL no corpo (como no v67). Parametrizá-lo é tentador, mas
 * mudaria o motor — `IGH_COEF_EXEMPLO` (premissas.ts) existe no protótipo e não é usado
 * por ninguém.
 */
export function calcIGH(b: Empreendimento | null | undefined, lev: Lev, opts: OpcoesIGH): ResultadoIGH {
  const medAc = opts.medAcum, CTO = opts.CTO, ymD = opts.ymD;
  const GTO = Math.max(0, (1 - medAc) * CTO);                             /* GTO = (1 − Medição Acum.) × CTO */
  const det = Array.isArray(lev.unidadesDet) ? lev.unidadesDet : [];
  /* item 26 (09/10/2026): unidade a unidade só com venda/financiamento informado no Detalhamento */
  const temUnid = !vendasDetZeradas(lev);
  let fsfRec = 0;
  if (temUnid) {
    /* 08/10/2026 (EL-191): FSF a receber e estoque passam pela BASE DE CÁLCULO do pacote
       (unidade + vagas vinculadas de `vagasDet`): o menor entre venda e avaliação, salvo
       avaliação forçada. Antes: o FSF usava "venda; senão avaliação", só da unidade, e o
       estoque "avaliação; senão venda" + a avaliação das vagas. O `avalSenaoVenda` preserva
       a 2ª metade — aqui a unidade sem avaliação sempre valeu pela venda. Decisão de
       08/10/2026: as vagas vinculadas entram também no FSF a receber (o pacote). */
    det.forEach(u => {
      if (u.financiada) fsfRec += baseDet(lev, u).valor * ((u.percFin || 0) / 100);   /* FSF total a receber das financiadas */
    });
  }
  /* o estoque: a régua extraída para `estoqueNoMes` (15/10/2026), as mesmas somas na mesma ordem */
  const estoqueUnid = temUnid ? estoqueNoMes(b, lev, ymD) : 0;
  const SMPF = (temUnid ? fsfRec : (opts.fsfTotal || 0)) * (1 - medAc);   /* premissa: FSF liberado proporcional à medição */
  const SDA = opts.pjSacado || 0;                                        /* o PJ da barra: o empréstimo daquele mês */
  /**
   * ⚠ MESMA REGRA DO ESTÁTICO desde 01/08/2026: o mínimo é **1,30 × o EMPRÉSTIMO**.
   *
   * Antes era `(SMPJ + SDA) × 1,30`, com `SMPJ = GTO − SMPF`. Parece somar "o que falta
   * emprestar" com "o que já foi emprestado" — mas `serieGarantias` passa em `pjSacado` o
   * PJ **TOTAL** daquela barra, não a parcela já desembolsada. Somar os dois contava a
   * mesma exposição duas vezes, e o mínimo saía perto do dobro. É exatamente o defeito que
   * a v35 já tinha corrigido no estático zerando o SDA, e que ficou de pé aqui.
   *
   * `SMPJ` continua sendo devolvido para a tela mostrar de onde vem a necessidade de
   * financiamento, mas não entra mais na conta do mínimo.
   */
  const SMPJ = Math.max(0, GTO - SMPF);
  const minEstoque = SDA * 1.30;
  /* item 26 (09/10/2026): sem Detalhamento, a régua do EL-078 — era `opts.estoqueFallback`
     (as garantias totais da barra, usadas como substituto do estoque) */
  const estoque = temUnid ? estoqueUnid : _estoquePelaContagem(b, lev, ymD);
  return { medAc, GTO, SMPF, SDA, SMPJ, minEstoque, estoque, cumpre: estoque >= minEstoque - 0.5, temUnid };
}

/**
 * A GARANTIA EM ESTOQUE NO MÊS `ymD` — extraída de `calcIGH` em 15/10/2026 (Fluxo pelo memorial),
 * SEM mudar a conta, para o IGH de cada medição do Fluxo mensal (`engine/fluxo.ts`) usar a MESMA
 * régua das barras: com o Detalhamento preenchido, a base EL-191 de cada unidade ainda não
 * registrada (+ as vagas soltas); zerado, a contagem das Premissas Mensais × VGV ÷ unidades.
 *
 * Decisão de 14/10/2026 ("Manter a base EL-191"): o estoque do memorial é o "valor avaliado"; aqui
 * segue a base EL-191, como em todo o resto do motor (plano, P9).
 *
 * @param saiu  quando a unidade do Detalhamento saiu do estoque. Padrão: a régua das barras (registro = a
 *   "Data de Financiamento" que vale + EL-146). O fluxo passa a dele (o registro PROJETADO do lote — 15/10/2026).
 */
export function estoqueNoMes(
  b: Empreendimento | null | undefined, lev: Lev, ymD: Ym | null,
  saiu: (u: Unidade, i: number) => boolean = (u) => _saiuEstoqueTempo(lev, u, ymD),
): number {
  if (vendasDetZeradas(lev)) return _estoquePelaContagem(b, lev, ymD);
  const det = Array.isArray(lev.unidadesDet) ? lev.unidadesDet : [];
  /* Parte B (29/07/2026): as vagas avulsas do lado EL-147 entram no estoque com a mesma
     lógica da Parte A — vinculada acompanha a unidade, solta é estoque, permutada+informada
     à Caixa sai. ⚠ Lê `lev.vagasDet` (e NÃO `_vagasVinc`, que resolve sempre contra
     `vagasDem`): aqui as unidades vêm do lado `det`, e os ids de `u.vagas` são de `vagasDet`.
     Cruzar as duas tabelas somaria avaliação de outra base. */
  const vgs = Array.isArray(lev.vagasDet) ? lev.vagasDet : [];
  const idsDe = (u: Unidade) => (Array.isArray(u.vagas) ? u.vagas : []);
  const vinculadas = new Set<string>();
  det.forEach(u => { idsDe(u).forEach(id => vinculadas.add(id)); });
  let estoque = 0;
  det.forEach((u, i) => {
    if (saiu(u, i)) return;                                                           /* saiu: contrato registrado (ou permuta informada) */
    estoque += baseDet(lev, u).valor;                                                 /* garantia em estoque: a unidade + as vagas que ficam com ela */
  });
  /* vaga avulsa não vinculada = não vendida = estoque (decisão de 28/07: "Todas as vagas
     avulsas não vendidas entram no cálculo de composição de estoque"), pelo menor valor */
  vgs.forEach(v => { if (!_vagaExcl(v) && !vinculadas.has(v.id)) estoque += baseDaVaga(v); });
  return estoque;
}

/**
 * A GARANTIA EM ESTOQUE DO MÊS SEM DETALHAMENTO — lote 2 de 09/10/2026, item 26.
 *
 *     estoque = máx(0, unidades − Σ "Vendas Fin." até o mês − permutadas) × VGV ÷ unidades
 *
 * É a régua do "realizado" do gráfico do EL-078 (`ighSerie`), escrita com as MESMAS
 * expressões e somando os meses na MESMA ordem (`lev.meses`, do primeiro ao mês da barra) —
 * é o que faz a barra e o gráfico do mesmo mês darem o mesmo número, bit a bit
 * (provado em `tests/golden/motor.test.ts`). Só vendas FINANCIADAS saem, no mês da venda;
 * as "Vendas não fin." não tiram ninguém do estoque. Sem mês (`ymD` nulo) ninguém sai.
 */
function _estoquePelaContagem(b: Empreendimento | null | undefined, lev: Lev, ymD: Ym | null): number {
  const U = b ? b.unidades : 0, preco = U ? (b as Empreendimento).VGV / U : 0, perm = (b && b.permutadas) || 0;
  let repAc = 0;
  for (const m of vendasMensais(lev)) { if (ymD != null && m.ym <= ymD) repAc += (m.vendFin || 0); }
  return Math.max(0, U - repAc - perm) * preco;
}

/**
 * A BARRA DE UM MÊS — composição de garantias e o PJ daquele mês (EL-110).
 *
 * Extraída de `serieGarantias` em 08/10/2026, SEM mudar uma conta, para que o mínimo do
 * IGH do gráfico do EL-078 (`ighSerie`) use EXATAMENTE o mesmo PJ que a barra mostra. As
 * somas acumuladas percorrem `lev.meses` na mesma ordem de antes — é o que mantém os
 * números idênticos byte a byte.
 */
export function _barraDoMes(b: Empreendimento, lev: Lev, c: ResultadoBase, ym: Ym) {
  /* 20/10/2026 ("um OU outro"): as vendas que valem — com o Detalhamento preenchido, a contagem das unidades */
  const meses = vendasMensais(lev);
  let acum = lev.pls0Pct || 0; for (const m of meses) { if (m.ym <= ym) acum += (m.obra || 0); }
  let sold = 0; for (const m of meses) { if (m.ym <= ym) sold += (m.vendFin || 0) + (m.vendNao || 0); }
  let rep = 0; for (const m of meses) { if (m.ym <= ym) rep += (m.vendFin || 0); }
  const obra = (acum / 100) * c.CTO;
  /* 31/07/2026: o FSF da barra passou a usar `repasseDaUnidade`, a MESMA fórmula do
     estático e do fluxo. Era `rep × preço × %`, sem descontar a fração do terreno.
     Lote 2 de 09/10/2026, item 16: com o Detalhamento preenchido, FSF e terreno somam
     UNIDADE A UNIDADE (a mesma soma da tela de Premissas) — ver `_fsfTerrenoDaBarra`. */
  const ft = _fsfTerrenoDaBarra(b, lev, c, ym, rep);
  const fsf = ft.fsf;
  const terreno = lev.terrenoGarantia ? ft.terreno : 0, aporte = lev.aporte || 0;
  const gar = fsf + aporte + terreno + obra;
  const pjLGCmax = (lev.lgcPct > 0) ? ((b.lgcDisponivel || 0) / (lev.lgcPct / 100)) : Infinity;   /* item 4: teto de PJ pela LGC */
  const lgcCap = lev.pjLimitadoLGC ? pjLGCmax : Infinity;
  /* EL-192 (09/10/2026): o "PJ limite" do empreendimento, o mesmo 4º teto do estático e do calcBase */
  const pj = Math.max(0, Math.min(c.CTO - gar, lgcCap, c.tetoPJ, tetoInformadoPJ(b)));            /* v45 item 11 */
  /* lote 3 de 09/10/2026 (itens 7 e 29): os TERMOS do Math.min acima, e o PJ sem o "PJ limite", saem
     junto para a memória de cálculo da barra dizer quem limitou o PJ — mostrar, sem refazer a conta.
     O `pj` continua sendo a linha acima, intocada (provado byte a byte em tests/barra-detalhe.test.ts). */
  const tetos = { necessidade: c.CTO - gar, lgc: lgcCap, normativo: c.tetoPJ, informado: tetoInformadoPJ(b) };
  const pjSemLimite = Math.max(0, Math.min(c.CTO - gar, lgcCap, c.tetoPJ));
  return { acum, sold, rep, obra, fsf, terreno, aporte, gar, pj, porUnidade: ft.porUnidade, detalhe: ft, tetos, pjSemLimite };
}

/**
 * A BARRA DE UM MÊS, COM AS PARCELAS — lote 3 de 09/10/2026, itens 7 e 29.
 *
 * Pedidos literais: "Insira a memória de cálculo em EL-111 também." e "Espelhar formato da
 * memória de cálculo que há em EL-124 em EL-127." A memória de cálculo da barra (só Admin Hype)
 * LÊ daqui o que o motor fez em `_barraDoMes` — a MESMA função que produziu o número da barra —,
 * do jeito que a da tela de Composição lê `fsfDetalhe`/`terrenoDetalhe`: unidade a unidade quando
 * o Detalhamento tem venda, a média quando está zerado (item 26) e o excedente das "Vendas Fin."
 * sem unidade identificada; e os termos do PJ. Não muda número nenhum: os totais são os da barra.
 *
 * `partes`/`parteEntram`: a parte de cada unidade no terreno, pela MESMA regra do
 * `terrenoDetalhe` (fração ÷ Σ frações; sem fração nenhuma, 1 ÷ unidades totais).
 */
export function barraDetalhe(b: Empreendimento, lev: Lev, c: ResultadoBase, ym: Ym): DetalheBarra {
  const x = _barraDoMes(b, lev, c, ym);
  const ft = x.detalhe, d = ft.det;
  const U = b.unidades || 0;
  const semFracao = d ? !(d.totalFrac > 0) : true;
  return {
    ym, acum: x.acum, sold: x.sold, rep: x.rep, obra: x.obra, fsf: x.fsf, terreno: x.terreno, aporte: x.aporte,
    gar: x.gar, pj: x.pj,
    zerado: ft.zerado, media: ft.media, fracaoMedia: c.fracao,
    identificadas: ft.identificadas, porUnidade: ft.porUnidade, excedente: ft.excedente, foraDaBarra: ft.foraDaBarra,
    fsfUnidades: d ? d.total : 0, fsfExcedente: ft.fsfExcedente,
    terrenoUnidades: d ? d.terreno : 0, terrenoExcedente: ft.terrenoExcedente, terrenoBruto: ft.terreno,
    itens: d ? d.itens : [], totalFrac: d ? d.totalFrac : 0, fracEntram: d ? d.fracEntram : 0, semFracao,
    partes: d ? d.itens.map(it => (semFracao ? (U ? 1 / U : 0) : it.frac / d.totalFrac)) : [],
    parteEntram: d ? (semFracao ? (U ? d.itens.length / U : 0) : d.fracEntram / d.totalFrac) : 0,
    tetos: x.tetos, pjSemLimite: x.pjSemLimite,
  };
}

/**
 * FSF E TERRENO DA BARRA DE UM MÊS — lote 2 de 09/10/2026, item 16 ("Unidade a unidade
 * (Recomendado)").
 *
 * Pedido literal: "Aparentemente, mesmo espelhando dados em EL-154 e EL-043, há uma
 * divergência nos resultados. Use o empreendimento Verus, cenário realista, para fazer essa
 * análise." A divergência era de REGRA: a barra usava a média (preço médio × % padrão −
 * fração média) e a tela de Premissas, a soma por unidade (base EL-191, % próprio, fração
 * ideal). Agora:
 *
 *   - Detalhamento ZERADO (`vendasDetZeradas`, item 26): pela média, como antes —
 *     `rep × repasse(VGV ÷ U, % padrão, terreno ÷ U)` e `rep × terreno ÷ U`.
 *   - Detalhamento preenchido: as unidades do Detalhamento VENDIDAS FINANCIADAS até o mês
 *     (financiada, não permutada, mês da "Data de venda" ≤ mês da barra — a mesma contagem
 *     das "Vendas Fin." que o "Salvar" do Detalhamento escreve nas Premissas Mensais) somam
 *     pela MESMA conta da Premissas (`fsfDetalheDe`, lado do Detalhamento).
 *
 * CONCILIAÇÃO com as "Vendas Fin." das Premissas Mensais (`rep`), que continuam editáveis:
 *   - rep MAIOR que as identificadas: o excedente entra pela média, como hoje;
 *   - rep MENOR: entram as `rep` unidades de "Data de venda" mais antiga (empate: a ordem da
 *     tabela) — e a soma corre na ordem da tabela, a mesma da Premissas.
 * Em fórmula: `n = mín(identificadas, ⌊rep⌋)` somam por unidade e `rep − n` pela média.
 *
 * Mês sem nenhuma identificada (n = 0) dá EXATAMENTE a conta de antes (`0 + rep × média`).
 *
 * ⚠ LOTE 4 DE 10/10/2026, ITEM 5: exportada para o `calcFluxo` (engine/motor.ts). Com o
 * Detalhamento preenchido, o FSF e a fração do terreno da tabela "Fluxo mensal" (EL-145) saem
 * DAQUI, mês a mês — a MESMA soma da barra, sem fórmula nova. O `terreno` devolvido é o BRUTO
 * (sem o liga/desliga do "Terreno como garantia?", que `_barraDoMes` aplica por fora). A demanda
 * do fluxo também sai daqui (zerado ou não, desde a revisão do lote 4) — salvo quando as Premissas
 * Mensais não têm nenhuma "Vendas Fin." (`premissasMensaisSemVendasFin`, decisão do usuário de
 * 11/10/2026, "O campo da demanda"): aí o `calcFluxo` usa o campo "Unidades financiadas na demanda".
 */
export function _fsfTerrenoDaBarra(b: Empreendimento, lev: Lev, c: ResultadoBase, ym: Ym, rep: number) {
  const media = repasseDaUnidade(c.preco, lev.pctFin, c.fracao);
  /* lote 3 de 09/10/2026 (itens 7 e 29): além do total, as parcelas — para a memória de cálculo da
     barra (`barraDetalhe`). As expressões de `fsf` e `terreno` são as de antes, na mesma ordem */
  if (vendasDetZeradas(lev)) {
    return { fsf: rep * media, terreno: c.fracao * rep, porUnidade: 0,
      zerado: true, media, identificadas: 0, excedente: rep, fsfExcedente: rep * media, terrenoExcedente: c.fracao * rep,
      det: null as DetalheFSF | null, foraDaBarra: [] as Array<{ i: number; nome: string }> };
  }
  const lado = ladoDetalhamento(lev);
  const vendidas: Array<{ i: number; ymV: number; d: string }> = [];
  lado.unidades.forEach((u, i) => {
    if (!u || !u.financiada || u.permutada || !u.dataVenda) return;
    const y = ymOf(u.dataVenda);
    if (y != null && y <= ym) vendidas.push({ i, ymV: y, d: u.dataVenda });   /* data ilegível: NaN ≤ ym é falso */
  });
  /* 20/10/2026 — "um caminho OU outro" (revisão do PR SAAS-984, comentário 1): com o Detalhamento preenchido
     (modo individual), a barra soma TODAS as unidades vendidas e financiadas até o mês, cada uma pelo seu valor.
     Acabou a conciliação com as "Vendas Fin." (o excedente pela média e as identificadas fora da barra): elas
     agora SÃO a contagem das unidades (`vendasMensais`). `rep` fica só para o modo rápido. */
  void rep;
  const entram = new Set(vendidas.map(x => x.i));
  const d = fsfDetalheDe(b, lev, lado, (_u, i) => entram.has(i));
  return { fsf: d.total, terreno: d.terreno, porUnidade: vendidas.length,
    zerado: false, media, identificadas: vendidas.length, excedente: 0, fsfExcedente: 0, terrenoExcedente: 0,
    det: d as DetalheFSF | null, foraDaBarra: [] as Array<{ i: number; nome: string }> };
}

/**
 * v67 linhas 1618-1631 — série mês a mês para o modal EL-078 (v36).
 * realizado = estoque de unidades não repassadas/permutadas × avaliação média.
 *
 * ⚠ O MÍNIMO MUDOU em 08/10/2026 (aprovado: "Corrigir, em passo separado"): era
 * `1,30 × máx(0, GTO − FSF)`, a regra que o estático abandonou em 01/08/2026. Agora é
 * **1,30 × o PJ daquele mês** — o mesmo PJ da barra do EL-110 (`_barraDoMes`), o mesmo
 * `SDA × 1,30` que `calcIGH` usa na barra. Com a regra antiga, um FSF menor SUBIA o
 * mínimo, e o gráfico do EL-078 e a barra do mesmo mês exigiam números diferentes.
 * `SMPJ` passa a ser esse PJ (no estático, `igh.SMPJ` também é o PJ desde 01/08).
 */
export function ighSerie(b: Empreendimento, lev: Lev, c: ResultadoBase): PontoIGH[] | null {
  if (!lev.dataContratacao || !lev.meses || !lev.meses.length) return null;
  const U = b.unidades, preco = U ? b.VGV / U : 0, CTO = c.CTO, perm = b.permutadas || 0;
  let medAc = (lev.pls0Pct || 0) / 100, repAc = 0; const s: PontoIGH[] = [];
  vendasMensais(lev).forEach(m => {   /* 20/10/2026: as vendas que valem ("um OU outro") */
    medAc += (m.obra || 0) / 100; repAc += (m.vendFin || 0);
    const GTO = Math.max(0, (1 - Math.min(1, medAc)) * CTO);
    const SMPJ = _barraDoMes(b, lev, c, m.ym).pj;              /* o PJ do mês — o mesmo da barra */
    const min = SMPJ * 1.30;                                  /* = `SDA × 1,30` de calcIGH, na mesma ordem */
    const real = Math.max(0, U - repAc - perm) * preco;       /* estoque de unidades ainda não repassadas (nem permutadas) */
    s.push({ ym: m.ym, min, real, GTO, SMPJ });
  });
  return s;
}

/**
 * O PRIMEIRO MÊS QUE O "IGH PÓS-DEMANDA" OLHA — lote 4 de 10/10/2026, item 1.
 *
 * Pedido literal: "IGH pós-demanda deve olhar apenas os meses após o cumprimento de demanda."
 * Decisão: "A partir do mês do Cumprimento (Recomendado)" — com o Cumprimento em nov.26, a
 * verificação olha nov.26 em diante (o próprio mês do Cumprimento entra).
 *
 * Sem "Cumprimento de demanda" — ou com uma data ilegível, que `ymOf` devolve como `NaN` —
 * devolve `null`: a verificação olha a operação inteira, a regra da v67. Data ilegível NÃO cala
 * a verificação (`x.ym >= NaN` daria falso em todo mês, e nenhum descumprimento seria visto).
 *
 * É a régua do `ighAvalia` e a que a tela lê para dizer de que mês em diante a verificação olha
 * (a linha do EL-111, a memória de cálculo e o gráfico do EL-078) — uma regra só.
 */
export function ighPosDemandaDesde(lev: Lev): Ym | null {
  const y = lev && lev.dataDemandaDesejada ? ymOf(lev.dataDemandaDesejada) : null;
  return y != null && Number.isFinite(y) ? y : null;
}

/**
 * v67 linhas 1632-1636 — 1º mês em que o realizado fura o mínimo (tolerância 0,5).
 *
 * ⚠ LOTE 4 DE 10/10/2026, ITEM 1: só conta mês a partir do "Cumprimento de demanda"
 * (`ighPosDemandaDesde`); sem Cumprimento, a operação inteira, como na v67. A SÉRIE (`serieAt`)
 * continua inteira — o gráfico do EL-078 mostra todos os meses e marca os anteriores ao
 * Cumprimento como fora da verificação. Só `riscoAtual`/`breachAtYm` mudam, e por consequência
 * o `sIGH` e a cor das barras (provado em tests/golden/motor.test.ts, bloco "item 1").
 *
 * ⚠ REVISÃO DO LOTE 4 (10/10/2026): se as Premissas Mensais TERMINAM antes do mês do Cumprimento,
 * nenhum mês é olhado — a verificação não tem o que verificar. Antes isso passava como "sem
 * descumprimento" (✓ vazio, e o texto afirmava uma previsão que não foi feita). Agora devolve
 * `semMesesAvaliados: true`. A chave só aparece quando é verdadeira: a saída dos casos com mês
 * olhado é a de antes.
 *
 * ⚠ DECISÃO DO USUÁRIO (11/10/2026, caixa — resposta literal "Neutro, com aviso (Recomendado)"): sem
 * mês olhado a verificação é NEUTRA — não reprova (`riscoAtual` false, `breachAtYm` null), então o
 * `sIGH` fica verdadeiro e a cor da barra é a das outras verificações (a barra de out.26 do
 * Verus/Realista, rascunho, continua verde). A revisão do lote 4 (b7ddc7c) tinha contado isso como
 * risco (barra amarela) por padrão; a tela é que diz "não verificável", em tom neutro, fora da conta
 * do selo, e manda estender as Premissas Mensais até o mês do Cumprimento.
 */
export function ighAvalia(b: Empreendimento, lev: Lev, c: ResultadoBase): AvaliacaoIGH | null {
  const serieAt = ighSerie(b, lev, c); if (!serieAt) return null;
  const desde = ighPosDemandaDesde(lev);
  const idxAt = serieAt.findIndex(x => (desde == null || x.ym >= desde) && x.real < x.min - 0.5);
  const semMesesAvaliados = desde != null && !serieAt.some(x => x.ym >= desde);
  return {
    serieAt, riscoAtual: idxAt >= 0, breachAtYm: idxAt >= 0 ? serieAt[idxAt].ym : null,
    ...(semMesesAvaliados ? { semMesesAvaliados: true as const } : {}),
  };
}

/**
 * v67 linhas 2001-2032 — gráfico de garantias mês a mês (EL-110/EL-111): uma barra por
 * mês do cronograma, com composição de garantias, PJ e os 9 semáforos.
 *
 * @param ighRisco  vem de `ighAvalia(...).riscoAtual`; alimenta o semáforo soft `sIGH` — desde o
 *                  lote 4 de 10/10/2026 (item 1), "descumpre a partir do mês do Cumprimento de demanda".
 */
export function serieGarantias(
  b: Empreendimento,
  lev: Lev,
  c: ResultadoBase,
  ighRisco: boolean,
): BarraGarantia[] | null {
  if (!lev.dataContratacao) return null;
  /* comportamento preservado do protótipo: `start` é VARIÁVEL MORTA (v67 linha 2003) —
     calculada e nunca usada. Mantida para que a extração seja literal. */
  const start = ymOf(lev.dataContratacao); void start;
  const meses = lev.meses || [];
  const ymDes = lev.dataDemandaDesejada ? ymOf(lev.dataDemandaDesejada) : null;
  const lgcDisp = b.lgcDisponivel || 0;
  const out: BarraGarantia[] = [];
  for (let k = 0; k < meses.length; k++) {   /* v38: uma barra por mês do cronograma (EL-070) */
    const ym = meses[k].ym;
    /* 08/10/2026: a composição da barra saiu para `_barraDoMes`, sem mudar uma conta — o
       gráfico do EL-078 (`ighSerie`) passou a precisar do MESMO PJ do mês. */
    const { acum, sold, rep, obra, fsf, terreno, aporte, gar, pj } = _barraDoMes(b, lev, c, ym);
    const lgcNec = pj * ((lev.lgcPct || 0) / 100);
    /* item 4 (v33): IGH real do md por barra (na data da barra) */
    /* item 26 (09/10/2026): sem `estoqueFallback` — sem Detalhamento, o estoque da barra é a
       régua do EL-078 (`_estoquePelaContagem`), não mais as garantias da própria barra */
    const _igh = calcIGH(b, lev, { CTO: c.CTO, ymD: ym, medAcum: Math.min(1, acum / 100), pjSacado: pj, fsfTotal: c.valorFinDem });
    const ighReal = _igh.estoque, ighMin = _igh.minEstoque;
    /* parâmetros duros (vermelho se algum falha); v45 fix: tolerância 0,5 no LGC */
    const hGar = (gar + pj) >= c.CTO - 0.5, hVend = sold >= c.salesMin, hRep = rep >= c.finMin, hLGC = lgcNec <= lgcDisp + 0.5, hIGH = _igh.cumpre;
    const hardOk = hGar && hVend && hRep && hLGC && hIGH;
    /* parâmetros internos do cliente (amarelo se algum falha) */
    /* ⚠ LOTE 2 DE 09/10/2026 — o critério "LGC tomado ≤ LGC desejado" (`sLGC`, R$ 2 mi por
       padrão, sem campo na tela) SAIU DA COR: decisão "Tirar da cor (Recomendado)". As
       Verificações da barra não o mostram desde a v31, e ele pintava barras de amarelo sem
       dizer por quê (no Verus/Realista, a barra de out.26). Continua calculado em `p.sLGC`,
       só para informação — não pesa na cor nem, por consequência, na escolha da 1ª barra verde. */
    const sLGC = ((lev.lgcLimiteDesejado as number) > 0) ? lgcNec <= (lev.lgcLimiteDesejado as number) : true;
    const sPrazo = (ymDes != null) ? ym <= ymDes : true;
    /* CAL-2b (12/10/2026): folga de resíduo binário da soma da obra (1e-9 ponto percentual,
       decisão D5), a mesma do motivo de obra do calcViab (motor.ts). A barra com "15,0% / mín
       15%" ficava amarela por 14.999999999999998. Só o resíduo: 14,96% continua amarelo.
       DIVERGÊNCIA INTENCIONAL DA v67 só nessa faixa; nenhum caso de tests/golden cai nela. */
    const sObra = acum >= (lev.minObraPct || 0) - 1e-9;
    const sIGH = !ighRisco;
    const softOk = sPrazo && sObra && sIGH;
    const color = !hardOk ? 'red' : (!softOk ? 'yellow' : 'green');
    out.push({ ym, acum, fsf, aporte, terreno, obra, gar, pj, lgcNec, sold, rep, ighReal, ighMin, color, p: { hGar, hVend, hRep, hLGC, hIGH, sLGC, sPrazo, sObra, sIGH } });
  }
  return out;
}
